import { observer } from 'mobx-react-lite';
import robuxPriceHistoryStore from '@store/robux-price-history-store.ts';
import SlideToggle from '@components/core/slide-toggle/slide-toggle.tsx';
import { IconEnum } from '@components/core/icon/icon-enum.ts';
import { Experience, FullExperience } from '@/schemas/experience-schema.ts';
import AnnotationSelectorEnum from '@components/data/annotation-selector/annotation-selector-enum.ts';
import annotationStateStore from '@store/annotation-state-store.ts';
import Icon from '@components/core/icon/icon.tsx';
import themeStore from '@store/theme-store.ts';
import ClassString from '@utils/class-string.ts';
import Tooltip from '@components/core/tooltip/tooltip.tsx';

interface AnnotationSelectorProps {
	experience: Experience;
	className?: string;
}

const AnnotationSelector = observer(function AnnotationSelector(
	props: AnnotationSelectorProps
) {
	const fullExperience = props.experience as FullExperience;
	const dailyPickDates = fullExperience.daily_pick_dates ?? [];

	return (
		<div
			className={ClassString({
				static: 'flex justify-center items-center gap-6 select-none',
				dynamic: {},
				custom: props.className,
			})}
		>
			{!!robuxPriceHistoryStore.experienceRobuxPriceHistory(
				props.experience.place_id
			).length && (
				<Tooltip
					text={'Robux Price'}
					direction={'bottom'}
					widthClass={'whitespace-nowrap'}
				>
					<div
						className={
							'flex justify-center items-center gap-2 text-center overflow-x-visible'
						}
					>
						<img
							src={
								themeStore.lightTheme
									? '/robux-black.svg'
									: '/robux-white.svg'
							}
							alt={''}
							className={'h-5 w-5'}
						/>
						<SlideToggle
							state={annotationStateStore.showRobuxPrice}
							smallButton
							icons={{
								on: IconEnum.CONFIRM,
								onSize: '1.5rem',
								off: IconEnum.CLEAR,
								offSize: '1.5rem',
							}}
							onToggle={(show) => {
								annotationStateStore.setState(
									show,
									AnnotationSelectorEnum.ROBUX_HISTORY
								);
							}}
							toggleBackgroundColor
						/>
					</div>
				</Tooltip>
			)}

			{!!dailyPickDates.length && (
				<Tooltip
					text={'Daily Picks'}
					direction={'bottom'}
					widthClass={'whitespace-nowrap'}
				>
					<div className={'flex justify-center items-center gap-2'}>
						<Icon icon={IconEnum.DAILY_PICKS} size={'1.25rem'} />
						<SlideToggle
							state={annotationStateStore.showDailyPicks}
							smallButton
							icons={{
								on: IconEnum.CONFIRM,
								onSize: '1.5rem',
								off: IconEnum.CLEAR,
								offSize: '1.5rem',
							}}
							onToggle={(show) => {
								annotationStateStore.setState(
									show,
									AnnotationSelectorEnum.DAILY_PICKS
								);
							}}
							toggleBackgroundColor
						/>
					</div>
				</Tooltip>
			)}

			<Tooltip text={'Icons'} direction={'bottom'}>
				<div className={'flex justify-center items-center gap-2'}>
					<div
						className={
							'w-4 min-w-4 h-4 min-h-4 bg-gray-600 dark:bg-white rounded-xs'
						}
					></div>
					<SlideToggle
						state={annotationStateStore.showIcons}
						smallButton
						icons={{
							on: IconEnum.CONFIRM,
							onSize: '1.5rem',
							off: IconEnum.CLEAR,
							offSize: '1.5rem',
						}}
						onToggle={(show) => {
							annotationStateStore.setState(
								show,
								AnnotationSelectorEnum.ICONS
							);
						}}
						toggleBackgroundColor
					/>
				</div>
			</Tooltip>

			<Tooltip text={'Thumbnails'} direction={'bottom'}>
				<div className={'flex justify-center items-center gap-2'}>
					<div
						className={
							'w-7 min-w-7 h-4 min-h-4 bg-gray-600 dark:bg-white rounded-xs'
						}
					></div>
					<SlideToggle
						state={annotationStateStore.showBanners}
						smallButton
						icons={{
							on: IconEnum.CONFIRM,
							onSize: '1.5rem',
							off: IconEnum.CLEAR,
							offSize: '1.5rem',
						}}
						onToggle={(show) => {
							annotationStateStore.setState(
								show,
								AnnotationSelectorEnum.BANNERS
							);
						}}
						toggleBackgroundColor
					/>
				</div>
			</Tooltip>
		</div>
	);
});

export default AnnotationSelector;
